import { FC } from 'react';
import { Link } from '~/router';

// Detects a stale-bundle error from React Router's lazy() / dynamic import.
// Vite emits content-addressed JS chunks under /assets/ with hashes in the
// filename. After a deploy, the new index.html points to NEW filenames; a
// browser tab still holding the OLD index.html will try to load the old
// chunk filenames. If those have been removed from S3 (or evicted from the
// CDN), the dynamic import fails with one of the messages below. The fix
// is always "reload to get the fresh index.html" — see the deploy workflows
// (.github/workflows/deploy-*.yml) for the asset-retention strategy that
// minimises (but doesn't fully eliminate) this case.
const isChunkLoadError = (error: unknown): boolean => {
  if (!error) return false;
  const message =
    error instanceof Error
      ? error.message
      : typeof error === 'string'
        ? error
        : '';
  return (
    /Failed to fetch dynamically imported module/i.test(message) ||
    /error loading dynamically imported module/i.test(message) ||
    /Importing a module script failed/i.test(message) ||
    /ChunkLoadError/i.test(message)
  );
};

const BrokenDownCarAnimation = () => {
  return (
    <div className="mb-8 select-none" aria-hidden="true">
      <style>{`
        @keyframes broken-rattle {
          0%, 100% { transform: translate(0, 0) rotate(0deg); }
          20%      { transform: translate(-1px, 1px) rotate(-0.6deg); }
          40%      { transform: translate(1px, -1px) rotate(0.6deg); }
          60%      { transform: translate(-1px, 0) rotate(-0.4deg); }
          80%      { transform: translate(1px, 1px) rotate(0.4deg); }
        }
        @keyframes broken-smoke {
          0%   { transform: translate(0, 0) scale(0.6); opacity: 0; }
          20%  { opacity: 0.8; }
          100% { transform: translate(-12px, -42px) scale(1.4); opacity: 0; }
        }
        @keyframes broken-warning {
          0%, 100% { transform: translateY(0) scale(1); }
          50%      { transform: translateY(-3px) scale(1.08); }
        }
        @keyframes broken-cloud-drift {
          0%   { transform: translateX(0); }
          100% { transform: translateX(-320px); }
        }
        .broken-car { animation: broken-rattle 0.4s ease-in-out infinite; }
        .broken-smoke-1 { animation: broken-smoke 2.4s ease-out infinite; }
        .broken-smoke-2 { animation: broken-smoke 2.4s ease-out 0.6s infinite; }
        .broken-smoke-3 { animation: broken-smoke 2.4s ease-out 1.2s infinite; }
        .broken-smoke-4 { animation: broken-smoke 2.4s ease-out 1.8s infinite; }
        .broken-warning { animation: broken-warning 1.2s ease-in-out infinite; }
        .broken-cloud { animation: broken-cloud-drift 18s linear infinite; }
      `}</style>

      <div
        className="relative mx-auto overflow-hidden rounded-lg"
        style={{
          height: 176,
          width: 320,
          background: 'linear-gradient(to bottom, #f0f9ff, #ffffff)'
        }}
      >
        {/* Drifting clouds */}
        <div
          className="broken-cloud absolute top-3 left-0 flex"
          style={{ width: 640, gap: 80 }}
        >
          <div className="w-12 h-3 bg-white rounded-full opacity-80" />
          <div className="w-16 h-4 bg-white rounded-full opacity-70" />
          <div className="w-10 h-3 bg-white rounded-full opacity-80" />
          <div className="w-14 h-3 bg-white rounded-full opacity-75" />
          <div className="w-12 h-3 bg-white rounded-full opacity-80" />
        </div>

        {/* Road */}
        <div className="absolute bottom-4 left-0 right-0 h-0.5 bg-gray-300" />
        <div className="absolute bottom-3 left-0 right-0 flex justify-around">
          {Array.from({ length: 10 }).map((_, i) => (
            <div key={i} className="w-4 h-0.5 bg-gray-200" />
          ))}
        </div>

        {/* Smoke puffs rising from the engine */}
        <div
          className="absolute"
          style={{ bottom: 36, left: '50%', marginLeft: 14 }}
        >
          <div className="broken-smoke-1 absolute w-3 h-3 bg-gray-400 rounded-full opacity-0" />
          <div className="broken-smoke-2 absolute w-3 h-3 bg-gray-300 rounded-full opacity-0" />
          <div className="broken-smoke-3 absolute w-2 h-2 bg-gray-400 rounded-full opacity-0" />
          <div className="broken-smoke-4 absolute w-3 h-3 bg-gray-300 rounded-full opacity-0" />
        </div>

        {/* Warning triangle floating above */}
        <div
          className="broken-warning absolute"
          style={{ bottom: 92, left: '50%', marginLeft: -8 }}
        >
          <svg width="20" height="18" viewBox="0 0 20 18">
            <path
              d="M10 1 L19 17 L1 17 Z"
              fill="#f59e0b"
              stroke="#b45309"
              strokeWidth="1.5"
              strokeLinejoin="round"
            />
            <rect x="9" y="6" width="2" height="6" rx="1" fill="#ffffff" />
            <rect x="9" y="13" width="2" height="2" rx="1" fill="#ffffff" />
          </svg>
        </div>

        {/* Broken-down car (stationary, rattling) */}
        <div
          className="broken-car absolute bottom-4"
          style={{ left: '50%', marginLeft: -28 }}
        >
          <svg width="56" height="36" viewBox="0 0 56 36">
            {/* Body */}
            <rect x="2" y="17" width="52" height="13" rx="3" fill="#3b82f6" />
            {/* Open hood (tilted up) */}
            <path
              d="M42 17 L52 4 L54 5 L44 17 Z"
              fill="#60a5fa"
              stroke="#3b82f6"
              strokeWidth="1"
              strokeLinejoin="round"
            />
            {/* Roof */}
            <path
              d="M14 17 L18 8 L36 8 L40 17"
              fill="#60a5fa"
              stroke="#3b82f6"
              strokeWidth="1"
            />
            {/* Windows */}
            <path d="M19 9 L16 16 L26 16 L26 9 Z" fill="#bfdbfe" />
            <path d="M28 9 L28 16 L38 16 L35 9 Z" fill="#bfdbfe" />
            {/* Engine block under open hood */}
            <rect x="44" y="18" width="8" height="6" rx="1" fill="#1f2937" />
            {/* Tail light */}
            <rect x="1" y="20" width="2" height="4" rx="1" fill="#ef4444" />
            {/* Front wheel */}
            <g>
              <circle cx="40" cy="30" r="5" fill="#374151" />
              <circle cx="40" cy="30" r="2" fill="#9ca3af" />
              <line
                x1="40"
                y1="26"
                x2="40"
                y2="34"
                stroke="#6b7280"
                strokeWidth="0.5"
              />
              <line
                x1="36"
                y1="30"
                x2="44"
                y2="30"
                stroke="#6b7280"
                strokeWidth="0.5"
              />
            </g>
            {/* Rear wheel */}
            <g>
              <circle cx="14" cy="30" r="5" fill="#374151" />
              <circle cx="14" cy="30" r="2" fill="#9ca3af" />
              <line
                x1="14"
                y1="26"
                x2="14"
                y2="34"
                stroke="#6b7280"
                strokeWidth="0.5"
              />
              <line
                x1="10"
                y1="30"
                x2="18"
                y2="30"
                stroke="#6b7280"
                strokeWidth="0.5"
              />
            </g>
          </svg>
        </div>
      </div>
    </div>
  );
};

const ErrorFallback: FC<{ error: unknown }> = ({ error }) => {
  // In dev, always show the raw error so we can debug — never paper over it
  // with the chunk-load "new version available" UI.
  const isDev = import.meta.env.DEV;
  const chunkLoadError = !isDev && isChunkLoadError(error);

  const title = chunkLoadError
    ? 'A new version is available'
    : 'Something went wrong';

  const description = chunkLoadError
    ? "We've shipped an update since you opened this page. Reload to get the latest version."
    : 'Our little car has broken down on the way. Try reloading the page — if it keeps happening, please let us know.';

  const errorMessage =
    error instanceof Error
      ? error.message
      : typeof error === 'string'
        ? error
        : null;

  const errorStack = error instanceof Error ? error.stack : null;

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4">
      <div className="w-full max-w-md rounded-lg bg-white p-8 shadow-lg text-center">
        <BrokenDownCarAnimation />

        <h1 className="mb-2 text-3xl font-bold text-gray-900">{title}</h1>

        <p className="mb-6 text-gray-600">{description}</p>

        {!chunkLoadError && errorMessage && (
          <pre className="mb-6 overflow-auto rounded bg-gray-100 p-3 text-left text-xs text-gray-700 whitespace-pre-wrap">
            {errorMessage}
            {isDev && errorStack ? `\n\n${errorStack}` : ''}
          </pre>
        )}

        <div className="flex flex-col items-center gap-3">
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="inline-block w-full rounded-md bg-blue-500 px-6 py-2 font-semibold text-white shadow hover:bg-blue-600 transition-colors"
          >
            Reload page
          </button>

          {!chunkLoadError && (
            <Link
              to="/dashboard"
              className="text-sm text-gray-500 hover:text-gray-700 transition-colors"
            >
              Back to dashboard
            </Link>
          )}
        </div>
      </div>
    </div>
  );
};

export default ErrorFallback;
